import React, { useState, useEffect } from 'react';
import api from '../api';

const ScheduleManager = () => {
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [scheduleEntries, setScheduleEntries] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newEntry, setNewEntry] = useState({
        classId: '',
        startTime: '',
        endTime: '',
        title: '',
        description: ''
    });

    useEffect(() => {
        fetchClasses();
    }, []);

    useEffect(() => {
        if (selectedDate) {
            fetchScheduleForDate(selectedDate);
        }
    }, [selectedDate]);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchScheduleForDate = async (date) => {
        try {
            setLoading(true);
            const res = await api.get(`/schedules/date/${date}`);
            setScheduleEntries(res.data);
        } catch (error) {
            console.error('Error fetching schedule:', error);
            setScheduleEntries([]);
        } finally {
            setLoading(false);
        }
    };

    const getDayOfWeek = (dateString) => {
        const date = new Date(dateString);
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[date.getDay()];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('vi-VN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleUpdateEntry = async (entryId, updates) => {
        try {
            const res = await api.put(`/schedules/${entryId}`, updates);
            setScheduleEntries(prev =>
                prev.map(entry => entry._id === entryId ? res.data : entry)
            );
            setEditingEntry(null);
            alert('Cập nhật thành công!');
        } catch (error) {
            console.error('Error updating schedule entry:', error);
            alert('Có lỗi xảy ra khi cập nhật!');
        }
    };

    const handleCreateEntry = async () => {
        if (!newEntry.classId || !newEntry.startTime || !newEntry.endTime) {
            alert('Vui lòng điền đầy đủ thông tin bắt buộc!');
            return;
        }

        try {
            const entryData = {
                ...newEntry,
                date: selectedDate,
                dayOfWeek: getDayOfWeek(selectedDate)
            };

            const res = await api.post('/schedules', entryData);
            setScheduleEntries(prev => [...prev, res.data].sort((a, b) =>
                a.startTime.localeCompare(b.startTime)
            ));
            setNewEntry({
                classId: '',
                startTime: '',
                endTime: '',
                title: '',
                description: ''
            });
            setShowCreateForm(false);
            alert('Tạo lịch học thành công!');
        } catch (error) {
            console.error('Error creating schedule entry:', error);
            alert('Có lỗi xảy ra khi tạo lịch học!');
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa lịch học này?')) {
            return;
        }

        try {
            await api.delete(`/schedules/${entryId}`);
            setScheduleEntries(prev => prev.filter(entry => entry._id !== entryId));
            alert('Xóa lịch học thành công!');
        } catch (error) {
            console.error('Error deleting schedule entry:', error);
            alert('Có lỗi xảy ra khi xóa lịch học!');
        }
    };

    const generateScheduleForClass = async (classId) => {
        const startDate = selectedDate;
        const endDate = new Date(selectedDate);
        endDate.setMonth(endDate.getMonth() + 1); // Generate for next month

        try {
            const res = await api.post(`/schedules/generate/${classId}`, {
                startDate: startDate,
                endDate: endDate.toISOString().split('T')[0]
            });

            alert(`Đã tạo ${res.data.entries.length} lịch học tự động!`);
            fetchScheduleForDate(selectedDate);
        } catch (error) {
            console.error('Error generating schedule:', error);
            alert('Có lỗi xảy ra khi tạo lịch học tự động!');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-slate-900">Quản lý lịch học chi tiết</h2>
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
                >
                    <i className="fas fa-plus" aria-hidden></i>
                    Thêm lịch học
                </button>
            </div>

            {/* Date Selector */}
            <div className="rounded-2xl bg-white p-6 border border-slate-200">
                <div className="flex items-center gap-4 mb-4">
                    <label className="text-sm font-medium text-slate-700">
                        Chọn ngày:
                    </label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                    <span className="text-sm text-slate-600 font-medium">
                        {formatDate(selectedDate)}
                    </span>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            const today = new Date();
                            setSelectedDate(today.toISOString().split('T')[0]);
                        }}
                        className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                    >
                        Hôm nay
                    </button>
                    <button
                        onClick={() => {
                            const tomorrow = new Date();
                            tomorrow.setDate(tomorrow.getDate() + 1);
                            setSelectedDate(tomorrow.toISOString().split('T')[0]);
                        }}
                        className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                    >
                        Ngày mai
                    </button>
                    <button
                        onClick={() => {
                            const nextWeek = new Date();
                            nextWeek.setDate(nextWeek.getDate() + 7);
                            setSelectedDate(nextWeek.toISOString().split('T')[0]);
                        }}
                        className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                    >
                        Tuần tới
                    </button>
                </div>
            </div>

            {/* Create New Entry Form */}
            {showCreateForm && (
                <div className="rounded-2xl bg-slate-50 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Tạo lịch học mới</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Lớp học *
                            </label>
                            <select
                                value={newEntry.classId}
                                onChange={(e) => setNewEntry({ ...newEntry, classId: e.target.value })}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                required
                            >
                                <option value="">Chọn lớp học</option>
                                {classes.map(cls => (
                                    <option key={cls._id} value={cls._id}>{cls.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Giờ bắt đầu *
                                </label>
                                <input
                                    type="time"
                                    value={newEntry.startTime}
                                    onChange={(e) => setNewEntry({ ...newEntry, startTime: e.target.value })}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Giờ kết thúc *
                                </label>
                                <input
                                    type="time"
                                    value={newEntry.endTime}
                                    onChange={(e) => setNewEntry({ ...newEntry, endTime: e.target.value })}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Tiêu đề bài học
                            </label>
                            <input
                                type="text"
                                value={newEntry.title}
                                onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                                placeholder="Ví dụ: Bài 15 - Định lý Pythagore"
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Mô tả bài học
                            </label>
                            <textarea
                                value={newEntry.description}
                                onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                                placeholder="Ví dụ: Learn about pythagore theorem and its applications"
                                rows={3}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={handleCreateEntry}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
                        >
                            <i className="fas fa-save" aria-hidden></i>
                            Tạo lịch học
                        </button>
                        <button
                            onClick={() => {
                                setShowCreateForm(false);
                                setNewEntry({
                                    classId: '',
                                    startTime: '',
                                    endTime: '',
                                    title: '',
                                    description: ''
                                });
                            }}
                            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                        >
                            Hủy
                        </button>
                    </div>
                </div>
            )}

            {/* Schedule Entries */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">
                        Lịch học ngày {formatDate(selectedDate)}
                    </h3>
                </div>

                {loading ? (
                    <div className="text-center py-8">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                        <p className="mt-2 text-sm text-slate-600">Đang tải...</p>
                    </div>
                ) : scheduleEntries.length === 0 ? (
                    <div className="text-center py-8">
                        <i className="fas fa-calendar-times text-4xl text-slate-300 mb-4"></i>
                        <p className="text-slate-500">Không có lịch học nào trong ngày này</p>
                        <p className="text-sm text-slate-400 mt-1">Thêm lịch học mới hoặc tạo tự động từ lịch học cố định</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-200">
                        {scheduleEntries.map((entry) => (
                            <div key={entry._id} className="p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                                                <span className="text-sm font-medium text-indigo-600">
                                                    {entry.startTime} - {entry.endTime}
                                                </span>
                                            </div>
                                            <span className="text-lg font-semibold text-slate-900">
                                                {entry.classId?.name || 'Unknown Class'}
                                            </span>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                entry.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                                entry.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {entry.status === 'scheduled' ? 'Đã lên lịch' :
                                                 entry.status === 'completed' ? 'Đã hoàn thành' : 'Đã hủy'}
                                            </span>
                                        </div>

                                        {editingEntry === entry._id ? (
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                                        Tiêu đề bài học
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={entry.title || ''}
                                                        onChange={(e) => {
                                                            setScheduleEntries(prev =>
                                                                prev.map(item =>
                                                                    item._id === entry._id
                                                                        ? { ...item, title: e.target.value }
                                                                        : item
                                                                )
                                                            );
                                                        }}
                                                        placeholder="Ví dụ: Bài 15 - Định lý Pythagore"
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                                        Mô tả bài học
                                                    </label>
                                                    <textarea
                                                        value={entry.description || ''}
                                                        onChange={(e) => {
                                                            setScheduleEntries(prev =>
                                                                prev.map(item =>
                                                                    item._id === entry._id
                                                                        ? { ...item, description: e.target.value }
                                                                        : item
                                                                )
                                                            );
                                                        }}
                                                        placeholder="Ví dụ: Learn about pythagore theorem and its applications"
                                                        rows={3}
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleUpdateEntry(entry._id, {
                                                            title: entry.title,
                                                            description: entry.description
                                                        })}
                                                        className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-green-700"
                                                    >
                                                        <i className="fas fa-save" aria-hidden></i>
                                                        Lưu
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingEntry(null);
                                                            fetchScheduleForDate(selectedDate); // Refresh to reset changes
                                                        }}
                                                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow transition hover:bg-slate-50"
                                                    >
                                                        Hủy
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                {entry.title && (
                                                    <h4 className="font-medium text-slate-900 mb-1">{entry.title}</h4>
                                                )}
                                                {entry.description ? (
                                                    <p className="text-slate-600 text-sm">{entry.description}</p>
                                                ) : (
                                                    <p className="text-slate-400 text-sm italic">Chưa có mô tả bài học</p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 ml-4">
                                        {editingEntry !== entry._id && (
                                            <button
                                                onClick={() => setEditingEntry(entry._id)}
                                                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-indigo-700"
                                            >
                                                <i className="fas fa-edit" aria-hidden></i>
                                                Chỉnh sửa
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDeleteEntry(entry._id)}
                                            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-red-700"
                                        >
                                            <i className="fas fa-trash" aria-hidden></i>
                                            Xóa
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Actions for Classes */}
            {classes.length > 0 && (
                <div className="rounded-2xl bg-white p-6 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Tạo lịch học tự động</h3>
                    <p className="text-sm text-slate-600 mb-4">
                        Tạo lịch học tự động cho 1 tháng tới dựa trên lịch học cố định của lớp
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {classes.map(cls => (
                            <button
                                key={cls._id}
                                onClick={() => generateScheduleForClass(cls._id)}
                                className="text-left p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition"
                            >
                                <div className="font-medium text-slate-900 text-sm">{cls.name}</div>
                                <div className="text-xs text-slate-500">
                                    {cls.schedules?.length || 0} lịch cố định
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleManager;